import { useEffect, useState } from 'react';
import { MapPin, ExternalLink, Copy, Check, WifiOff } from 'lucide-react';
import { direccionLegible, tieneCoordenadas } from './direcciones';

// La ubicación de una sucursal. SIN MAPA INCRUSTADO.
//
// AQUÍ HABÍA UN `<iframe>` DE GOOGLE MAPS, y se fue el 19 de agosto de
// 2026 (deuda R-2). El motivo no es de diseño: era un tercero cargándose
// DENTRO del producto, y con dato de cliente de por medio. La URL se
// construía con la dirección de la sucursal —`?q=<la dirección>&
// output=embed`—, así que cada vez que alguien abría esta pantalla,
// Google recibía LA DIRECCIÓN FÍSICA de la sucursal de un cliente de
// TEGO, la IP de quien miraba y el momento. Sin que nadie lo pidiera y
// sin que nadie lo supiera.
//
// Eso choca de frente con la regla de la casa —«que nadie tenga datos de
// mis clientes»— y no lo cubre la única excepción concedida, que es para
// el canal de avisos y precisamente porque por ahí no viaja ni un dato.
//
// LO QUE QUEDA ES MEJOR DE LO QUE PARECE. La ficha con la dirección, las
// coordenadas copiables y el enlace ya estaban aquí debajo, y sirven con
// o sin internet — incluso para dictar la dirección por teléfono. El
// enlace **no filtra nada hasta que alguien lo pulsa**: entonces sí, pero
// es una decisión de la persona y no una fuga de fondo.
//
// El recuadro de arriba dice DÓNDE está la sucursal sin pedirle nada a
// nadie: la dirección, dibujada con las fichas de la propia consola.
export default function MapaSucursal({ sucursal, alto = 220 }) {
  const [online, setOnline] = useState(navigator.onLine);
  const [copiado, setCopiado] = useState(false);

  useEffect(() => {
    const alCambiar = () => setOnline(navigator.onLine);
    window.addEventListener('online', alCambiar);
    window.addEventListener('offline', alCambiar);
    return () => {
      window.removeEventListener('online', alCambiar);
      window.removeEventListener('offline', alCambiar);
    };
  }, []);

  const coords = tieneCoordenadas(sucursal)
    ? `${sucursal.latitud},${sucursal.longitud}`
    : null;
  const direccion = direccionLegible(sucursal);
  const consulta = coords || direccion;

  if (!consulta) {
    return (
      <p className="text-sm text-gray-500">
        Esta sucursal no tiene dirección ni coordenadas. Edítala para añadirlas.
      </p>
    );
  }

  const enlace = `https://www.google.com/maps?q=${encodeURIComponent(consulta)}`;

  async function copiar() {
    try {
      await navigator.clipboard.writeText(coords || direccion);
      setCopiado(true);
      setTimeout(() => setCopiado(false), 2000);
    } catch {
      // Sin portapapeles (contexto no seguro) el texto sigue visible y
      // seleccionable: no hace falta avisar de nada.
    }
  }

  return (
    <div className="space-y-2">
      {/* EL RECUADRO DE LA UBICACIÓN · dibujado aquí, sin pedir nada.
          `alto` se respeta para que la pantalla no dé un salto al pasar
          de la versión con mapa a esta. */}
      <div
        style={{ height: alto }}
        className="w-full rounded-xl border border-gray-200 bg-gray-50 flex flex-col
                   items-center justify-center gap-2 px-6 text-center"
      >
        <MapPin className="h-6 w-6 text-gray-400" aria-hidden="true" />
        <span className="text-sm font-semibold text-gray-800">
          {sucursal.nombre}
        </span>
        {/* La dirección NO se repite aquí: vive una sola vez, en la ficha
            de abajo, donde además se puede copiar. Ponerla dos veces
            hacía leerla dos veces para comprobar que decían lo mismo. */}
        <span className="text-xs text-gray-500 max-w-[46ch]">
          El mapa se abre aparte, al pulsar. Mientras tanto esta dirección
          no sale de tu servidor.
        </span>
      </div>

      <div className="text-sm bg-white border border-gray-200 rounded-xl divide-y divide-gray-100">
        {direccion && (
          <div className="px-3 py-2 flex items-start gap-2">
            <MapPin className="h-4 w-4 text-gray-500 mt-0.5 shrink-0" />
            <span className="text-gray-800">{direccion}</span>
          </div>
        )}
        {coords && (
          <div className="px-3 py-2 flex items-center justify-between gap-3">
            <span className="font-mono text-xs text-gray-700">{coords}</span>
            <button
              onClick={copiar}
              className="inline-flex items-center gap-1 text-xs font-semibold text-[var(--color-marca)] hover:text-[var(--color-marca-fuerte)]"
            >
              {copiado ? <Check className="h-3.5 w-3.5" /> : <Copy className="h-3.5 w-3.5" />}
              {copiado ? 'Copiado' : 'Copiar'}
            </button>
          </div>
        )}
        {/* SIN INTERNET SE DICE, no se deja un enlace que lleva a una
            pantalla de error del navegador. La dirección de arriba sigue
            sirviendo, que es lo que hace falta para llegar. */}
        {online ? (
          <a
            href={enlace}
            target="_blank"
            rel="noreferrer noopener"
            className="px-3 py-2 flex items-center gap-1.5 text-sm font-semibold text-[var(--color-marca)] hover:bg-gray-50"
          >
            <ExternalLink className="h-3.5 w-3.5" /> Abrir en Google Maps
          </a>
        ) : (
          <p className="px-3 py-2 flex items-start gap-1.5 text-xs text-gray-500">
            <WifiOff className="h-3.5 w-3.5 shrink-0 mt-0.5" aria-hidden="true" />
            Sin salida a internet: el mapa no se puede abrir. La dirección y
            las coordenadas de arriba siguen sirviendo.
          </p>
        )}
      </div>
    </div>
  );
}
